import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PodcastsIcon from '@mui/icons-material/Podcasts';

const DURATION_OPTIONS = [3, 5, 10, 15, 20, 30] as const;

const LANGUAGE_OPTIONS = ['English', 'Spanish', 'French', 'German', 'Italian', 'Portuguese'] as const;

const VISIBILITY_OPTIONS = [
  { value: 'private', label: 'Private' },
  { value: 'public', label: 'Public' },
  { value: 'restricted', label: 'Restricted' },
] as const;

export type GeneratePodcastFromClusterDialogProps = {
  open: boolean;
  clusterCount: number;
  busy?: boolean;
  onClose: () => void;
  onConfirm: (opts: { duration: number; language: string; visibility: string }) => void;
};

export function GeneratePodcastFromClusterDialog({
  open,
  clusterCount,
  busy = false,
  onClose,
  onConfirm,
}: GeneratePodcastFromClusterDialogProps) {
  const [duration, setDuration] = useState<number>(5);
  const [language, setLanguage] = useState<string>('English');
  const [visibility, setVisibility] = useState<string>('private');

  useEffect(() => {
    if (!open) return;
    setDuration(5);
    setLanguage('English');
    setVisibility('private');
  }, [open]);

  const n = Math.max(0, clusterCount);

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#141418',
          color: '#fafafa',
          border: '1px solid #3f3f46',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Typography component="span" variant="h6" sx={{ fontWeight: 700 }}>
          Generate Podcast
        </Typography>
        <IconButton aria-label="Close" onClick={onClose} disabled={busy} size="small" sx={{ color: '#a1a1aa' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body2" sx={{ color: '#d4d4d8', mb: 2 }}>
          You are about to generate a podcast from {n === 1 ? '1 selected cluster' : `${n} selected clusters`}.
        </Typography>

        <Box
          sx={{
            border: '1px solid #3f3f46',
            borderRadius: 1,
            p: 2,
            mb: 2,
            bgcolor: '#0a0a0a',
          }}
        >
          <Typography variant="subtitle2" sx={{ color: '#fafafa', fontWeight: 600, mb: 2 }}>
            Podcast Settings
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <FormControl size="small" sx={{ flex: '1 1 140px', minWidth: 140 }}>
              <InputLabel id="gp-duration" sx={{ color: '#a1a1aa' }}>
                Podcast Length
              </InputLabel>
              <Select
                labelId="gp-duration"
                label="Podcast Length"
                value={duration}
                disabled={busy}
                onChange={(e) => setDuration(Number(e.target.value))}
                sx={{ color: '#fafafa', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#52525b' } }}
              >
                {DURATION_OPTIONS.map((m) => (
                  <MenuItem key={m} value={m}>
                    {m} min
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: '1 1 140px', minWidth: 140 }}>
              <InputLabel id="gp-lang" sx={{ color: '#a1a1aa' }}>
                Language
              </InputLabel>
              <Select
                labelId="gp-lang"
                label="Language"
                value={language}
                disabled={busy}
                onChange={(e: SelectChangeEvent) => setLanguage(e.target.value)}
                sx={{ color: '#fafafa', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#52525b' } }}
              >
                {LANGUAGE_OPTIONS.map((lang) => (
                  <MenuItem key={lang} value={lang}>
                    {lang}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <FormControl size="small" fullWidth sx={{ mb: 1 }}>
            <InputLabel id="gp-vis" sx={{ color: '#a1a1aa' }}>
              Visibility
            </InputLabel>
            <Select
              labelId="gp-vis"
              label="Visibility"
              value={visibility}
              disabled={busy}
              onChange={(e: SelectChangeEvent) => setVisibility(e.target.value)}
              sx={{ color: '#fafafa', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#52525b' } }}
            >
              {VISIBILITY_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {visibility === 'private' ? (
            <Typography variant="caption" sx={{ color: '#71717a', display: 'block' }}>
              Private podcasts are only visible to you.
            </Typography>
          ) : null}
        </Box>

        <Typography variant="body2" sx={{ color: '#a1a1aa', lineHeight: 1.5 }}>
          The podcast will be generated using AI based on the content of the selected clusters. This process may take a
          few moments.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 0 }}>
        <Button onClick={onClose} disabled={busy} sx={{ color: '#a1a1aa' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={busy || n < 1}
          onClick={() => onConfirm({ duration, language, visibility })}
          startIcon={<PodcastsIcon />}
          sx={{ bgcolor: '#2563eb', '&:hover': { bgcolor: '#1d4ed8' } }}
        >
          Generate podcast
        </Button>
      </DialogActions>
    </Dialog>
  );
}
